// Commissions admin UI — list + detail worksheet.
//
// URLs:
//   /admin/commissions            — list (filterable)
//   /admin/commissions/:id        — detail worksheet + action buttons

import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import {
  companyDisplayLabel,
  disbursementVocabulary,
  entityTypeLabel,
  isPeriodPoolDisbursement,
} from "@shared/comp/disbursement-vocabulary";
import {
  buildPaycheckLabels,
  splitPaychecks,
} from "@shared/comp/split-paychecks";

import {
  apiV11,
  type CommissionCostRec,
  type CommissionDisbursementRec,
  type CommissionPayrollRec,
  type DisbursementSourceLinkRec,
  type PayrollAdvisorItemRec,
} from "../api";
import { InsuranceRerunModal } from "../components/InsuranceRerunModal";
import { MortgageSummaryModal } from "../components/MortgageSummaryModal";
import { MarkPaidDialog } from "../components/MarkPaidDialog";
import type { PaidMethod } from "../../../shared/comp/paid-method";
import { formatCents } from "../utils/money";
import { buildWorksheetBreakdown } from "./commission-worksheet-breakdown";

type LoadState = "loading" | "error" | "ready";

// ─── List page ──────────────────────────────────────────────────────

// Deep-link filter validation — unknown values are ignored (falls back
// to "All"), so a mistyped/junk query param can never make the list
// silently show a filter state the selects don't reflect.
const LIST_COMPANIES = new Set([
  "mortgage",
  "insurance",
  "planning",
  "realty",
  "financial",
]);
const LIST_STATUSES = new Set(["pending", "calculated", "approved", "paid"]);

function sanitizeParam(raw: string | null, allowed: Set<string>): string {
  const v = (raw ?? "").trim().toLowerCase();
  return allowed.has(v) ? v : "";
}

export function CommissionsListPage() {
  const [state, setState] = useState<LoadState>("loading");
  const [items, setItems] = useState<CommissionDisbursementRec[]>([]);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  // Filters initialize from the URL so deep links prefilter — the Baker
  // dashboard's "?status=calculated" sign-off link and the cleanup
  // commission issue's "?company=…" flow link were silently ignored
  // before (the selects started at "All" regardless). Changing a select
  // writes the URL back (replace, not push) so the address bar never
  // lies about the filter in effect.
  const [searchParams, setSearchParams] = useSearchParams();
  const company = sanitizeParam(searchParams.get("company"), LIST_COMPANIES);
  const status = sanitizeParam(searchParams.get("status"), LIST_STATUSES);
  const setFilter = (key: "company" | "status", value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const load = useCallback(async () => {
    setState("loading");
    try {
      const params: { company?: string; status?: string } = {};
      if (company) params.company = company;
      if (status) params.status = status;
      const res = await apiV11.listCommissionDisbursements(params);
      setItems(res.items);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [company, status]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalDisb = items.reduce(
    (s, d) => s + d.advisor_disbursement_cents,
    0,
  );
  const totalNet = items.reduce((s, d) => s + d.net_income_cents, 0);

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1400, margin: "0 auto" }}>
      <h1
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontSize: 22,
          color: "var(--navy)",
          margin: 0,
          marginBottom: 12,
        }}
      >
        Commissions
      </h1>
      <MortgageSummaryModal open={summaryOpen} onClose={() => setSummaryOpen(false)} />
      <InsuranceRerunModal
        open={rerunOpen}
        onClose={() => setRerunOpen(false)}
        onChanged={() => void load()}
      />

      <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
        <select
          value={company}
          onChange={(e) => setFilter("company", e.target.value)}
          style={selectStyle}
        >
          <option value="">All companies</option>
          <option value="mortgage">Mortgage</option>
          <option value="insurance">Insurance</option>
          <option value="planning">Planning</option>
          <option value="realty">Realty</option>
          <option value="financial">Financial</option>
        </select>
        <select
          value={status}
          onChange={(e) => setFilter("status", e.target.value)}
          style={selectStyle}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="calculated">Calculated</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
        </select>
        {/* The one-click answer to "what do I owe each advisor this
            month" (Grant, 2026-08-11). Deliberately the first thing on
            the toolbar — it's the view payroll is actually run from. */}
        <Link to="/admin/commissions/payroll" style={{ textDecoration: "none" }}>
          <span style={pageBtn("primary")}>Payroll by advisor</span>
        </Link>
        {/* Pay runs (build step B3): shadow runs beside the page above,
            until Grant says go. */}
        <Link to="/admin/commissions/pay-runs" style={{ textDecoration: "none" }}>
          <span style={{ ...pageBtn("ghost"), minHeight: 44, display: "inline-flex", alignItems: "center" }}>Pay runs (shadow)</span>
        </Link>
        <button
          type="button"
          onClick={() => setRerunOpen(true)}
          style={{ ...pageBtn("ghost"), minHeight: 44 }}
          data-testid="insurance-rerun-open"
        >
          Re-run Insurance month
        </button>
        <button
          type="button"
          onClick={() => setSummaryOpen(true)}
          style={{ ...pageBtn("ghost"), minHeight: 44 }}
          data-testid="mortgage-summary-open"
        >
          Monthly Mortgage Summary
        </button>
        <div style={{ marginLeft: "auto", fontSize: 12, color: "var(--dark-gray)" }}>
          {/* Counts/sums over the rows currently listed — the server
              returns the full filtered set, not a page of it. */}
          {items.length} disbursements · Net{" "}
          <strong>{formatCents(totalNet)}</strong> · Disbursed{" "}
          <strong>{formatCents(totalDisb)}</strong>
        </div>
      </div>

      {state === "loading" && <div>Loading…</div>}
      {state === "error" && (
        <div style={{ color: "var(--danger)" }}>Couldn't load commissions.</div>
      )}
      {state === "ready" && (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            background: "var(--bg-0)",
            border: "1px solid var(--mid-gray)",
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <thead>
            <tr style={{ background: "var(--bg-1)", fontSize: 11, color: "var(--dark-gray)", textAlign: "left" }}>
              <th style={th}>Entity</th>
              <th style={th}>Company</th>
              <th style={{ ...th, textAlign: "right" }}>Gross</th>
              <th style={{ ...th, textAlign: "right" }}>Costs</th>
              <th style={{ ...th, textAlign: "right" }}>Net</th>
              <th style={{ ...th, textAlign: "right" }}>Disbursement</th>
              <th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id} style={{ borderTop: "1px solid var(--mid-gray)" }}>
                <td style={td}>
                  <Link
                    to={`/admin/commissions/${d.id}`}
                    style={{ color: "var(--navy)", fontWeight: 600, textDecoration: "none" }}
                  >
                    {d.display_label}
                  </Link>
                </td>
                <td style={td}>{companyDisplayLabel(d.company)}</td>
                <td style={{ ...td, textAlign: "right" }}>
                  {formatCents(d.gross_commission_cents)}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  {formatCents(d.total_transaction_costs_cents)}
                </td>
                <td style={{ ...td, textAlign: "right" }}>
                  {formatCents(d.net_income_cents)}
                </td>
                <td
                  style={{
                    ...td,
                    textAlign: "right",
                    fontWeight: 600,
                    color: "var(--navy)",
                  }}
                >
                  {formatCents(d.advisor_disbursement_cents)}
                </td>
                <td style={td}>
                  <StatusPill status={d.status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Payroll page ──────────────────────────────────────────────────
//
// Grant, 2026-08-11: "somewhere in the commissions section of the OS the
// user needs to be able to click a button or see in real time the amount
// due to each Advisor so that payroll can be run seamlessly."
//
// One row per advisor for one month, ending in the two paycheck amounts
// that get keyed into payroll. Defaults to the month that just ended,
// because commission for a month is run in the following one.
//
// Every figure is computed server-side by a SQL aggregate over the whole
// period — this page never adds up a list to produce a total, and it
// says out loud what is NOT in the total (worksheets still awaiting
// approval, and any disbursement carrying no period at all).

export function CommissionPayrollPage() {
  const [state, setState] = useState<LoadState>("loading");
  const [data, setData] = useState<CommissionPayrollRec | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [items, setItems] = useState<PayrollAdvisorItemRec[]>([]);
  const periodParam = searchParams.get("period") ?? "";

  const load = useCallback(async () => {
    setState("loading");
    try {
      const res = await apiV11.getCommissionPayroll(periodParam || undefined);
      setData(res);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [periodParam]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdvisor = async (advisorUserId: string, period: string) => {
    if (expanded === advisorUserId) {
      setExpanded(null);
      return;
    }
    setExpanded(advisorUserId);
    setItems([]);
    try {
      const res = await apiV11.getCommissionPayrollForAdvisor(
        advisorUserId,
        period,
      );
      setItems(res.items);
    } catch {
      setItems([]);
    }
  };

  return (
    <div style={{ padding: "24px 32px", maxWidth: 1400, margin: "0 auto" }}>
      <div style={{ fontSize: 11, color: "var(--dark-gray)", marginBottom: 4 }}>
        <Link to="/admin/commissions" style={{ color: "var(--blue-dark)" }}>
          Commissions
        </Link>{" "}
        · Payroll
      </div>
      <h1
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontSize: 22,
          color: "var(--navy)",
          margin: 0,
          marginBottom: 12,
        }}
      >
        Payroll — amount due each advisor
      </h1>

      {state === "loading" && <div>Loading…</div>}
      {state === "error" && (
        <div style={{ color: "var(--danger)" }}>Couldn't load payroll.</div>
      )}

      {state === "ready" && data && (
        <>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
            <label style={{ fontSize: 12, color: "var(--dark-gray)" }}>
              Month{" "}
              <select
                value={data.period}
                onChange={(e) => {
                  const next = new URLSearchParams(searchParams);
                  next.set("period", e.target.value);
                  setSearchParams(next, { replace: true });
                }}
                style={selectStyle}
              >
                {/* The active period is always selectable even when it has
                    no rows yet, so the picker can't silently jump months. */}
                {(data.available_periods.includes(data.period)
                  ? data.available_periods
                  : [data.period, ...data.available_periods]
                ).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--dark-gray)" }}>
              Ready for payroll{" "}
              <strong style={{ color: "var(--navy)" }}>
                {formatCents(data.totals.outstanding_cents)}
              </strong>{" "}
              across {data.totals.advisor_count}{" "}
              {data.totals.advisor_count === 1 ? "advisor" : "advisors"}
            </div>
          </div>

          {/* What is deliberately NOT in the number above. A total that
              quietly excludes unapproved work reads as "the month is
              done" when it isn't. */}
          {data.totals.awaiting_approval_count > 0 && (
            <div
              style={{
                padding: "10px 14px",
                marginBottom: 16,
                background: "var(--warning-bg)",
                border: "1px solid var(--mid-gray)",
                borderRadius: 10,
                fontSize: 12,
                color: "var(--navy)",
              }}
            >
              {data.totals.awaiting_approval_count}{" "}
              {data.totals.awaiting_approval_count === 1
                ? "worksheet is"
                : "worksheets are"}{" "}
              still awaiting approval, worth{" "}
              <strong>{formatCents(data.totals.awaiting_approval_cents)}</strong>.
              That is not in the total above and will move it once approved.
            </div>
          )}
          {data.unassigned.count > 0 && (
            <div
              style={{
                padding: "10px 14px",
                marginBottom: 16,
                background: "var(--warning-bg)",
                border: "1px solid var(--mid-gray)",
                borderRadius: 10,
                fontSize: 12,
                color: "var(--navy)",
              }}
            >
              {data.unassigned.count}{" "}
              {data.unassigned.count === 1 ? "disbursement isn't" : "disbursements aren't"}{" "}
              tied to a month ({formatCents(data.unassigned.cents)}), so{" "}
              {data.unassigned.count === 1 ? "it is" : "they are"} in no month's
              payroll total. Open{" "}
              <Link to="/admin/commissions" style={{ color: "var(--blue-dark)" }}>
                Commissions
              </Link>{" "}
              to set a period.
            </div>
          )}

          {data.rows.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--dark-gray)" }}>
              No commission activity recorded for {data.period}.
            </div>
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "var(--bg-0)",
                border: "1px solid var(--mid-gray)",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "var(--bg-1)",
                    fontSize: 11,
                    color: "var(--dark-gray)",
                    textAlign: "left",
                  }}
                >
                  <th style={th}>Advisor</th>
                  <th style={th}>Companies</th>
                  <th style={{ ...th, textAlign: "right" }}>Awaiting approval</th>
                  <th style={{ ...th, textAlign: "right" }}>Already paid</th>
                  <th style={{ ...th, textAlign: "right" }}>Due this payroll</th>
                  <th style={{ ...th, textAlign: "right" }}>Paycheck 1</th>
                  <th style={{ ...th, textAlign: "right" }}>Paycheck 2</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <>
                    <tr
                      key={r.advisor_user_id}
                      style={{ borderTop: "1px solid var(--mid-gray)", cursor: "pointer" }}
                      onClick={() => void openAdvisor(r.advisor_user_id, data.period)}
                    >
                      <td style={{ ...td, fontWeight: 600, color: "var(--navy)" }}>
                        {r.advisor_name}
                      </td>
                      <td style={{ ...td, color: "var(--dark-gray)" }}>
                        {r.companies.map(companyDisplayLabel).join(" · ") || "—"}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          color:
                            r.awaiting_approval_count > 0
                              ? "var(--danger)"
                              : "var(--dark-gray)",
                        }}
                      >
                        {r.awaiting_approval_count > 0
                          ? `${formatCents(r.awaiting_approval_cents)} (${r.awaiting_approval_count})`
                          : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right", color: "var(--dark-gray)" }}>
                        {r.paid_cents > 0 ? formatCents(r.paid_cents) : "—"}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontWeight: 700,
                          color: "var(--navy)",
                        }}
                      >
                        {formatCents(r.outstanding_cents)}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {r.paychecks_cents[0] !== undefined
                          ? formatCents(r.paychecks_cents[0])
                          : "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {r.paychecks_cents[1] !== undefined
                          ? formatCents(r.paychecks_cents[1])
                          : "—"}
                      </td>
                    </tr>
                    {expanded === r.advisor_user_id && (
                      <tr key={`${r.advisor_user_id}-items`}>
                        <td colSpan={7} style={{ padding: "8px 16px", background: "var(--bg-1)" }}>
                          {items.length === 0 ? (
                            <div style={{ fontSize: 12, color: "var(--dark-gray)" }}>
                              Loading the worksheets behind this total…
                            </div>
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                              {items.map((it) => (
                                <li key={it.id} style={{ padding: "3px 0" }}>
                                  <Link
                                    to={`/admin/commissions/${it.id}`}
                                    style={{ color: "var(--blue-dark)" }}
                                  >
                                    {it.display_label} ·{" "}
                                    {companyDisplayLabel(it.company)}
                                  </Link>{" "}
                                  — {formatCents(it.advisor_disbursement_cents)} ·{" "}
                                  <StatusPill status={it.status} />
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}

          <div style={{ marginTop: 12, fontSize: 11, color: "var(--dark-gray)" }}>
            "Due this payroll" is the approved commission not yet marked paid.
            The two paycheck amounts split it for the 15th and the last day of
            the month — the first rounds down and the second rounds up, so they
            always add back to the exact total.
          </div>
        </>
      )}
    </div>
  );
}

// ─── Worksheet page ────────────────────────────────────────────────

export function CommissionWorksheetPage() {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<LoadState>("loading");
  const [disb, setDisb] = useState<CommissionDisbursementRec | null>(null);
  const [costs, setCosts] = useState<CommissionCostRec[]>([]);
  const [sourceLink, setSourceLink] = useState<DisbursementSourceLinkRec | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setState("loading");
    try {
      const res = await apiV11.getCommissionDisbursement(id);
      setDisb(res.disbursement);
      setCosts(res.costs);
      setSourceLink(res.source_link);
      setPaidThroughGusto(res.paid_through_gusto);
      setState("ready");
    } catch {
      setState("error");
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const doCalculate = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await apiV11.calculateDisbursement(id, {});
      await load();
    } finally {
      setBusy(false);
    }
  };
  // Approving a per-loan mortgage worksheet also files its PDF onto the
  // loan's close-out checklist (Baker: "once approved, it populates in
  // the checklist"). Filing can fail without the approval failing, so
  // the outcome is reported here rather than swallowed.
  const [filedNote, setFiledNote] = useState<string | null>(null);
  const doApprove = async () => {
    if (!id) return;
    setBusy(true);
    setFiledNote(null);
    try {
      const res = await apiV11.approveDisbursement(id);
      if (res.filed?.ok) {
        setFiledNote(
          `Filed "${res.filed.filename}" on the loan's audit file under Disbursement.`,
        );
      } else if (res.filed) {
        setFiledNote(res.filed.message);
      }
      await load();
    } catch (err) {
      // A worksheet waiting for its deposit is refused: pay is priced only
      // from money received (Grant, 2026-10-24). The server says why.
      setFiledNote(err instanceof Error ? err.message : "The worksheet could not be approved.");
    } finally {
      setBusy(false);
    }
  };
  const doFileDocument = async () => {
    if (!id) return;
    setBusy(true);
    setFiledNote(null);
    try {
      const res = await apiV11.fileDisbursementDocument(id);
      setFiledNote(
        res.filed.ok
          ? `Filed "${res.filed.filename}" on the loan's audit file under Disbursement.`
          : res.filed.message,
      );
      await load();
    } finally {
      setBusy(false);
    }
  };
  // "Mark paid" opens a dialog (method picker + reference) instead of two
  // free-text prompts. The dialog shows the server's refusal and stays open.
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [paidThroughGusto, setPaidThroughGusto] = useState(false);
  const submitMarkPaid = async (method: PaidMethod, reference: string) => {
    if (!id) return;
    const payOpts: Parameters<typeof apiV11.markDisbursementPaid>[1] = {
      paid_method: method,
    };
    if (reference) payOpts.paid_reference = reference;
    await apiV11.markDisbursementPaid(id, payOpts);
    setMarkPaidOpen(false);
    await load();
  };

  if (state === "loading" || !disb) return <div style={{ padding: 32 }}>Loading…</div>;
  if (state === "error")
    return <div style={{ padding: 32, color: "var(--danger)" }}>Couldn't load.</div>;

  // Labels + cost rows come from the disbursement's OWN company. Before
  // this, every worksheet rendered Mortgage's nouns and Mortgage's cost
  // catalog regardless of company (Baker, 2026-07-31).
  const vocab = disbursementVocabulary(disb.company);
  const breakdown = buildWorksheetBreakdown(vocab, disb, costs);

  return (
    <div style={{ padding: "24px 32px", maxWidth: 900, margin: "0 auto" }}>
      <MarkPaidDialog
        open={markPaidOpen}
        paidThroughGusto={paidThroughGusto}
        onClose={() => setMarkPaidOpen(false)}
        onSubmit={submitMarkPaid}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          marginBottom: 4,
        }}
      >
        <div style={{ fontSize: 11, color: "var(--dark-gray)" }}>
          <Link to="/admin/commissions" style={{ color: "var(--blue-dark)" }}>
            Commissions
          </Link>{" "}
          · Worksheet
        </div>
        {/* Baker, 2026-09-01: one click back to the Processing record
            instead of re-finding it through the pipeline. Null for a
            monthly commission pool (Insurance/Financial) — a month has
            no single record to return to. */}
        {sourceLink && (
          <Link
            to={sourceLink.href}
            title={sourceLink.label}
            style={pageBtn("ghost")}
          >
            ← {sourceLinkButtonLabel(sourceLink.kind)}
          </Link>
        )}
      </div>
      <h1
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          fontSize: 22,
          color: "var(--navy)",
          margin: 0,
          marginBottom: 6,
        }}
      >
        Commission Worksheet
      </h1>
      <div style={{ color: "var(--dark-gray)", fontSize: 13, marginBottom: 4 }}>
        {disb.display_label} · {companyDisplayLabel(disb.company)}
        {disb.period_end && ` · Period ending ${disb.period_end}`}
      </div>
      {/* Raw entity id kept (smaller) — Baker uses it when matching a row
          back to the QB snapshot / loan file it was calculated from. */}
      <div style={{ color: "var(--dark-gray-light)", fontSize: 11, marginBottom: 24 }}>
        {disb.entity_id}
      </div>

      <div
        style={{
          padding: 20,
          background: "var(--bg-0)",
          border: "1px solid var(--mid-gray)",
          borderRadius: 10,
          marginBottom: 20,
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody>
            <WorksheetRow
              label={vocab.gross_label}
              cents={disb.gross_commission_cents}
              bold
            />
            {breakdown.show_costs_block && (
              <tr>
                <td
                  colSpan={2}
                  style={{
                    padding: "12px 12px 4px",
                    color: "var(--dark-gray)",
                    fontSize: 12,
                    fontStyle: "italic",
                  }}
                >
                  {vocab.costs_heading}
                </td>
              </tr>
            )}
            {/* Fixed rows this company's worksheet always shows, present or
                not (Mortgage only — Baker's paper document has all six
                lines). Other companies list only real costs. */}
            {breakdown.standard_rows.map((row) => (
              <WorksheetRow
                key={row.cost_type}
                label={`  ${humanizeCostType(row.cost_type)}`}
                cents={row.amount_cents}
                subtle
                muted={!row.present}
              />
            ))}
            {/* Cost rows outside this company's standard set — for Mortgage
                that's flood_cert / title / escrow; for every other company
                it's every cost row it has. */}
            {breakdown.extra_rows.map((c) => (
              <WorksheetRow
                key={c.id}
                label={`  ${humanizeCostType(c.cost_type)}${c.description ? ` · ${c.description}` : ""}`}
                cents={c.amount_cents}
                subtle
              />
            ))}
            {/* The rows above must add up to NET. When they don't, say so
                rather than showing a chain that silently doesn't
                reconcile — a number on this page is a promise. */}
            {breakdown.unitemized_cents !== 0 && (
              <WorksheetRow
                label="  Unitemized difference"
                cents={breakdown.unitemized_cents}
                subtle
                title={
                  `The itemized costs above total ${formatCents(breakdown.itemized_cents)}, ` +
                  `but ${vocab.gross_label} minus ${vocab.net_label} is ` +
                  `${formatCents(disb.gross_commission_cents - disb.net_income_cents)}. ` +
                  `This row is the difference.`
                }
              />
            )}
            <WorksheetRow
              label={vocab.net_label}
              cents={disb.net_income_cents}
              divider
              bold
            />
            <WorksheetRow
              label={`${vocab.split_label}${disb.advisor_split_bps !== null ? ` (${(disb.advisor_split_bps / 100).toFixed(1)}%)` : ""}`}
              cents={disb.advisor_disbursement_cents}
            />
            <WorksheetRow
              label={vocab.disbursement_label}
              cents={disb.advisor_disbursement_cents}
              bold
              divider
            />
            <WorksheetRow
              label={vocab.retained_label}
              cents={disb.brokerage_retained_cents}
              subtle
            />
          </tbody>
        </table>
      </div>

      {/* Baker's review step, in his words: "we double check the check
          amount versus what we were supposed to get, make sure credit
          fees are in there correctly, plus any other additional fees
          (rare but happens occasionally)". The second half of that
          sentence had nowhere to happen in the OS until now — costs
          existed in the schema and on the PDF but could only be set at
          creation time, so a worksheet generated from a check could
          never have its fees corrected before approval. */}
      {/* The Total Broker Check is read off the check once. When Stew
          misread it, or the wrong file was read first (2026-09-25: a
          $3,132.00 wire read in place of the correct $320.31 one), this
          is where an administrator corrects it by hand. The close-out
          card's "Re-read the check" is the other path. */}
      {disb.entity_type === "mortgage_loan" &&
        (disb.status === "pending" ||
          disb.status === "calculated" ||
          disb.status === "approved") && (
          <CheckAmountCorrector
            currentCents={disb.gross_commission_cents}
            grossLabel={vocab.gross_label}
            approved={disb.status === "approved"}
            busy={busy}
            onSave={async (grossCents, reason) => {
              if (!id) return;
              setBusy(true);
              setFiledNote(null);
              try {
                const r = await apiV11.restateDisbursementCheck(id, {
                  gross_commission_cents: grossCents,
                  reason,
                });
                const parts = [
                  r.changed
                    ? `${vocab.gross_label} is now ${formatCents(r.gross)}; NET ${formatCents(r.net_income)}, disbursement ${formatCents(r.advisor_disbursement)}.`
                    : `${vocab.gross_label} was already ${formatCents(r.gross)}, so nothing changed.`,
                ];
                if (r.was_approved) parts.push("The worksheet needs approving again.");
                if (r.superseded_document_id) {
                  parts.push("The Disbursement PDF filed on the old figures was pulled off the loan's checklist.");
                }
                setFiledNote(parts.join(" "));
                await load();
              } finally {
                setBusy(false);
              }
            }}
          />
        )}

      {(disb.status === "pending" || disb.status === "calculated") && (
        <CostEditor
          costs={costs}
          standardTypes={vocab.standard_cost_types}
          costsHeading={vocab.costs_heading}
          busy={busy}
          // Baker, 2026-08-11: he wants to enter the deductions by hand
          // on every loan, not auto-filled — so when nothing's been
          // entered yet, put the fee fields in front of him open rather
          // than behind an extra click on "Add transaction costs".
          defaultOpen={costs.length === 0}
          onSave={async (rows) => {
            if (!id) return;
            setBusy(true);
            try {
              await apiV11.calculateDisbursement(id, { costs: rows });
              await load();
            } finally {
              setBusy(false);
            }
          }}
        />
      )}

      <PaycheckSplitPanel
        disbursementCents={disb.advisor_disbursement_cents}
        periodEnd={disb.period_end}
        advisorRoleLabel={vocab.advisor_role_label}
        isPeriodPool={isPeriodPoolDisbursement(disb.entity_type)}
      />

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
        <button className="tt-floor"
          type="button"
          onClick={() => exportDisbursementCsv(disb, costs)}
          style={pageBtn("ghost")}
        >
          Export CSV
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16 }}>
        <StatusPill status={disb.status} />
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          {disb.status === "pending" && (
            <button className="tt-floor" type="button" onClick={doCalculate} disabled={busy} style={pageBtn("primary")}>
              Calculate
            </button>
          )}
          {disb.status === "calculated" && (
            <button className="tt-floor" type="button" onClick={doApprove} disabled={busy} style={pageBtn("primary")}>
              Approve
            </button>
          )}
          {(disb.status === "approved" || disb.status === "paid") &&
            disb.entity_type === "mortgage_loan" && (
              <button className="tt-floor"
                type="button"
                onClick={doFileDocument}
                disabled={busy}
                style={pageBtn("ghost")}
              >
                File the disbursement
              </button>
            )}
          {disb.status === "approved" && (
            <button className="tt-floor" type="button" onClick={() => setMarkPaidOpen(true)} disabled={busy} style={pageBtn("primary")}>
              Mark paid
            </button>
          )}
        </div>
      </div>

      {filedNote && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 16,
            background: "var(--bg-1)",
            border: "1px solid var(--mid-gray)",
            borderRadius: 10,
            fontSize: 12,
            color: "var(--dark-gray)",
          }}
        >
          {filedNote}
        </div>
      )}

      {/* QB reconciliation + audit trail */}
      <div
        style={{
          padding: 16,
          background: "var(--bg-1)",
          border: "1px solid var(--mid-gray)",
          borderRadius: 10,
        }}
      >
        <div style={{ color: "var(--dark-gray)", fontSize: 12 }}>
          {/* "CD" = Closing Disclosure, a mortgage closing document —
              this reconciliation step doesn't exist outside Mortgage
              (Baker, 2026-08-11).
              HELP-OK: bugfix hiding a mortgage-only row from other
              companies' worksheets — the commission help article never
              quoted this row's wording, so nothing it documents changed. */}
          {vocab.show_cd_reconciliation && (
            <div>
              <strong style={{ color: "var(--navy)" }}>CD gross matches:</strong>{" "}
              {disb.paid_reference ? "—" : disb.gross_commission_cents > 0 ? "check CD line manually" : "n/a"}
            </div>
          )}
          <div>
            <strong style={{ color: "var(--navy)" }}>QB deposit matches:</strong>{" "}
            {disb.paid_reference ?? "not reconciled"}
          </div>
          {disb.calculated_at && (
            <div style={{ marginTop: 6 }}>
              Calculated {new Date(disb.calculated_at).toLocaleString()}
            </div>
          )}
          {disb.approved_at && (
            <div>Approved {new Date(disb.approved_at).toLocaleString()}</div>
          )}
          {disb.paid_at && (
            <div>
              Paid {new Date(disb.paid_at).toLocaleString()}{" "}
              {disb.paid_method && `via ${disb.paid_method}`}{" "}
              {disb.paid_reference && `(${disb.paid_reference})`}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function WorksheetRow({
  label,
  cents,
  subtle,
  bold,
  divider,
  muted,
  title,
}: {
  label: string;
  cents: number;
  subtle?: boolean;
  bold?: boolean;
  divider?: boolean;
  muted?: boolean;
  title?: string;
}) {
  return (
    <tr
      {...(title ? { title } : {})}
      style={{
        borderTop: divider ? "2px solid var(--navy)" : undefined,
      }}
    >
      <td
        style={{
          padding: "8px 12px",
          color: muted ? "var(--dark-gray-light)" : subtle ? "var(--dark-gray)" : "var(--navy)",
          fontWeight: bold ? 700 : 400,
          whiteSpace: "pre",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: "8px 12px",
          textAlign: "right",
          color: muted ? "var(--dark-gray-light)" : subtle ? "var(--dark-gray)" : "var(--navy)",
          fontWeight: bold ? 700 : 400,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {cents === 0 && muted ? "$-" : formatCents(cents)}
      </td>
    </tr>
  );
}

// ─── Paycheck split ─────────────────────────────────────────────────
//
// The last mile of Baker's month. He reconciles the worksheet against
// the P&L, sends the advisor their report — and then has to run
// payroll, which needs the PER-CHECK amount, not the month's total.
// Before this panel the OS stopped at the total and he was, in his
// words, "left with nothing and I still need to [run] payroll and I
// don't have any guidance."
//
// The numbers come from splitPaychecks — the SAME function the emailed
// PDF uses — rather than a local halving, so the worksheet and the
// advisor's PDF can never disagree about what lands in a check. That
// matters beyond tidiness: the rule is round-down-then-up, so a naive
// ÷2 differs by a cent on any odd total (Brandon's July: 8,423.52 /
// 8,423.53, not 8,423.525 twice).
function PaycheckSplitPanel({
  disbursementCents,
  periodEnd,
  advisorRoleLabel,
  isPeriodPool,
}: {
  disbursementCents: number;
  periodEnd: string | null;
  advisorRoleLabel: string;
  isPeriodPool: boolean;
}) {
  // Per-record disbursements aren't a payroll item on their own, so they
  // get no split panel — inventing paycheck dates for one loan would
  // assert a pay cadence that nobody runs. They DO carry a period (so
  // they roll up), and the month's actual payroll figure lives on the
  // payroll view, which the header of this page links to.
  if (!isPeriodPool) return null;
  if (!periodEnd) return null;
  const period = periodEnd.slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(period)) return null;

  const split = splitPaychecks(disbursementCents, 2);
  const labels = buildPaycheckLabels(period);

  return (
    <div
      style={{
        padding: 20,
        background: "var(--bg-0)",
        border: "1px solid var(--mid-gray)",
        borderRadius: 10,
        marginBottom: 20,
      }}
      data-testid="commission-paycheck-split"
    >
      <div
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--navy)",
          marginBottom: 10,
        }}
      >
        Paycheck split
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {split.paychecks_cents.map((cents, i) => (
            <WorksheetRow
              key={labels[i] ?? `paycheck-${i}`}
              label={labels[i] ?? `Paycheck ${i + 1}`}
              cents={cents}
              bold
            />
          ))}
          <WorksheetRow
            label={`Total to ${advisorRoleLabel.toLowerCase()}`}
            cents={split.total_cents}
            subtle
            divider
          />
        </tbody>
      </table>
      <div style={{ marginTop: 10, fontSize: 11, color: "var(--dark-gray)" }}>
        Gross per check — enter these amounts in payroll. The first check
        rounds down and the second rounds up, so the two always add back
        to the exact total.
      </div>
    </div>
  );
}

// ─── Cost entry ─────────────────────────────────────────────────────
//
// The costs deducted from the check before the advisor's split. Rows
// come from the company's standard set (Mortgage's six, matching the
// paper document) plus whatever else is already recorded. Saving
// replaces the whole cost set and recalculates — the same
// /calculate endpoint the OS already used, now reachable by a human.
function centsToInput(cents: number): string {
  return cents === 0 ? "" : (cents / 100).toFixed(2);
}

/** "1,234.56" / "$1234.56" / "" → cents. Invalid text reads as 0. */
function inputToCents(raw: string): number {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return 0;
  if (!/^-?\d*(\.\d{0,2})?$/.test(cleaned)) return 0;
  const n = Number(cleaned);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function CostEditor({
  costs,
  standardTypes,
  costsHeading,
  busy,
  defaultOpen = false,
  onSave,
}: {
  costs: CommissionCostRec[];
  standardTypes: readonly string[];
  costsHeading: string;
  busy: boolean;
  /** Start expanded — used when nothing's been entered yet, so the fee
   *  fields Baker fills in by hand on every loan are already in front
   *  of him instead of behind an extra click. */
  defaultOpen?: boolean;
  onSave: (
    rows: Array<{ cost_type: string; description?: string; amount_cents: number }>,
  ) => Promise<void>;
}) {
  // The editable set: every standard row for this company (present or
  // not) plus any non-standard row that already has data, so opening the
  // editor can never silently drop a cost that was already recorded.
  const initial = useCallback(() => {
    const byType = new Map(costs.map((c) => [c.cost_type, c]));
    const rows = standardTypes.map((t) => ({
      cost_type: t,
      amount: centsToInput(byType.get(t)?.amount_cents ?? 0),
      description: byType.get(t)?.description ?? "",
    }));
    for (const c of costs) {
      if (!standardTypes.includes(c.cost_type)) {
        rows.push({
          cost_type: c.cost_type,
          amount: centsToInput(c.amount_cents),
          description: c.description ?? "",
        });
      }
    }
    return rows;
  }, [costs, standardTypes]);

  const [rows, setRows] = useState(initial);
  const [open, setOpen] = useState(defaultOpen);
  // Re-seed from the server whenever the saved costs change, so the
  // editor never shows a stale draft after a recalculation.
  useEffect(() => {
    setRows(initial());
  }, [initial]);

  const total = rows.reduce((s, r) => s + inputToCents(r.amount), 0);

  if (!open) {
    return (
      <div style={{ marginBottom: 20 }}>
        <button className="tt-floor" type="button" onClick={() => setOpen(true)} style={pageBtn("ghost")}>
          {costs.length === 0 ? "Add transaction costs" : "Edit transaction costs"}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 20,
        background: "var(--bg-0)",
        border: "1px solid var(--mid-gray)",
        borderRadius: 10,
        marginBottom: 20,
      }}
      data-testid="commission-cost-editor"
    >
      <div
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--navy)",
          marginBottom: 10,
        }}
      >
        {costsHeading}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {rows.map((row, i) => (
            <tr key={`${row.cost_type}-${i}`}>
              <td style={{ padding: "6px 12px", color: "var(--navy)" }}>
                {humanizeCostType(row.cost_type)}
              </td>
              <td style={{ padding: "6px 12px", textAlign: "right" }}>
                <input
                  type="text"
                  inputMode="decimal"
                  value={row.amount}
                  placeholder="0.00"
                  aria-label={`${humanizeCostType(row.cost_type)} amount`}
                  onChange={(e) => {
                    const next = [...rows];
                    next[i] = { ...row, amount: e.target.value };
                    setRows(next);
                  }}
                  style={{
                    width: 110,
                    padding: "6px 8px",
                    fontSize: 13,
                    textAlign: "right",
                    border: "1px solid var(--mid-gray)",
                    borderRadius: 6,
                  }}
                />
              </td>
            </tr>
          ))}
          <tr style={{ borderTop: "2px solid var(--navy)" }}>
            <td style={{ padding: "8px 12px", fontWeight: 700, color: "var(--navy)" }}>
              Total costs
            </td>
            <td
              style={{
                padding: "8px 12px",
                textAlign: "right",
                fontWeight: 700,
                color: "var(--navy)",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatCents(total)}
            </td>
          </tr>
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="tt-floor"
          type="button"
          disabled={busy}
          style={pageBtn("primary")}
          onClick={() =>
            void onSave(
              rows
                .map((r) => ({
                  cost_type: r.cost_type,
                  amount_cents: inputToCents(r.amount),
                  ...(r.description ? { description: r.description } : {}),
                }))
                // Zero rows aren't stored — the worksheet renders the
                // standard set as "$-" from the company vocabulary, so a
                // stored zero would add nothing but noise.
                .filter((r) => r.amount_cents !== 0),
            ).then(() => setOpen(false))
          }
        >
          Save costs &amp; recalculate
        </button>
        <button className="tt-floor"
          type="button"
          disabled={busy}
          style={pageBtn("ghost")}
          onClick={() => {
            setRows(initial());
            setOpen(false);
          }}
        >
          Cancel
        </button>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: "var(--dark-gray)" }}>
        Saving replaces the cost lines on this worksheet and recalculates the
        NET and the split. If this worksheet was already approved and its
        disbursement filed on the loan's audit file, that filed copy is pulled
        so a corrected one can replace it.
      </div>
    </div>
  );
}

// Correct a loan worksheet's Total Broker Check by hand. Collapsed by
// default: the figure is normally right, and an always-open money input
// invites edits nobody meant. A reason is required and is audit-logged
// with the before and after amounts.
function CheckAmountCorrector({
  currentCents,
  grossLabel,
  approved,
  busy,
  onSave,
}: {
  currentCents: number;
  grossLabel: string;
  approved: boolean;
  busy: boolean;
  onSave: (grossCents: number, reason: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(centsToInput(currentCents));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAmount(centsToInput(currentCents));
  }, [currentCents]);

  if (!open) {
    return (
      <div style={{ marginBottom: 12 }}>
        <button className="tt-floor" type="button" onClick={() => setOpen(true)} style={pageBtn("ghost")}>
          Correct the check amount
        </button>
      </div>
    );
  }

  const cents = inputToCents(amount);
  const canSave = cents > 0 && reason.trim().length > 0 && !busy;

  return (
    <div
      style={{
        padding: 20,
        background: "var(--bg-0)",
        border: "1px solid var(--mid-gray)",
        borderRadius: 10,
        marginBottom: 20,
      }}
      data-testid="commission-check-corrector"
    >
      <div
        style={{
          fontFamily: "Gotham, Montserrat, sans-serif",
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: "0.10em",
          textTransform: "uppercase",
          color: "var(--navy)",
          marginBottom: 10,
        }}
      >
        Correct the {grossLabel}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--dark-gray)" }}>
          Amount on the check
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            placeholder="0.00"
            onChange={(e) => setAmount(e.target.value)}
            style={{
              width: 140,
              minHeight: 44,
              padding: "6px 8px",
              fontSize: 14,
              textAlign: "right",
              border: "1px solid var(--mid-gray)",
              borderRadius: 6,
            }}
          />
        </label>
        <label
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            fontSize: 12,
            color: "var(--dark-gray)",
            flex: "1 1 240px",
          }}
        >
          Why is it changing?
          <input
            type="text"
            value={reason}
            maxLength={500}
            placeholder="e.g. The wrong wire was read first; the correct one shows $320.31"
            onChange={(e) => setReason(e.target.value)}
            style={{
              minHeight: 44,
              padding: "6px 8px",
              fontSize: 14,
              border: "1px solid var(--mid-gray)",
              borderRadius: 6,
            }}
          />
        </label>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
        <button className="tt-floor"
          type="button"
          disabled={!canSave}
          style={pageBtn("primary")}
          onClick={() => {
            setError(null);
            onSave(cents, reason.trim())
              .then(() => {
                setReason("");
                setOpen(false);
              })
              .catch((e: unknown) =>
                setError(e instanceof Error && e.message ? e.message : "Couldn't save the corrected amount."),
              );
          }}
        >
          Save {formatCents(cents)} &amp; recalculate
        </button>
        <button className="tt-floor"
          type="button"
          disabled={busy}
          style={pageBtn("ghost")}
          onClick={() => {
            setAmount(centsToInput(currentCents));
            setReason("");
            setError(null);
            setOpen(false);
          }}
        >
          Cancel
        </button>
      </div>
      {error ? (
        <div style={{ marginTop: 10, fontSize: 12, color: "var(--danger)" }}>{error}</div>
      ) : null}
      <div style={{ marginTop: 10, fontSize: 11, color: "var(--dark-gray)" }}>
        Currently {formatCents(currentCents)}. Saving keeps the transaction
        costs, recalculates NET and the split, and records your reason with
        the old and new amounts.
        {approved
          ? " This worksheet is approved: changing the amount undoes the approval and pulls the filed Disbursement PDF, so it needs approving again."
          : ""}
      </div>
    </div>
  );
}

function humanizeCostType(slug: string): string {
  return slug
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function exportDisbursementCsv(
  disb: CommissionDisbursementRec,
  costs: CommissionCostRec[],
) {
  const lines: string[] = [];
  const push = (...cells: (string | number)[]) =>
    lines.push(
      cells
        .map((c) =>
          typeof c === "string" && c.includes(",")
            ? `"${c.replace(/"/g, '""')}"`
            : String(c),
        )
        .join(","),
    );

  const dollars = (n: number) => `$${(n / 100).toFixed(2)}`;
  const today = new Date().toLocaleDateString("en-US");

  // Same company-scoped vocabulary the on-screen worksheet uses, so the
  // export Baker keys into Gusto matches the screen he approved.
  const vocab = disbursementVocabulary(disb.company);
  const breakdown = buildWorksheetBreakdown(vocab, disb, costs);

  push(vocab.csv_title);
  push("");
  push(vocab.advisor_role_label, "", disb.advisor_user_id, "", "", "Date:", today);
  push("");
  push(entityTypeLabel(disb.entity_type), disb.entity_id);
  push("");
  push("");
  push(vocab.gross_label, "", "", "", "", "", dollars(disb.gross_commission_cents));
  push("");
  if (breakdown.show_costs_block) {
    push(vocab.costs_heading);
    for (const row of breakdown.standard_rows) {
      push(
        humanizeCostType(row.cost_type),
        "",
        "",
        "",
        "",
        "",
        row.present ? dollars(row.amount_cents) : "$-",
      );
    }
    for (const c of breakdown.extra_rows) {
      push(
        `${humanizeCostType(c.cost_type)}${c.description ? ` · ${c.description}` : ""}`,
        "",
        "",
        "",
        "",
        "",
        dollars(c.amount_cents),
      );
    }
    if (breakdown.unitemized_cents !== 0) {
      push(
        "Unitemized difference",
        "",
        "",
        "",
        "",
        "",
        dollars(breakdown.unitemized_cents),
      );
    }
    push("");
  }
  push(vocab.net_label, "", "", "", "", "", dollars(disb.net_income_cents));
  push("");
  const pct =
    disb.advisor_split_bps !== null
      ? `${(disb.advisor_split_bps / 100).toFixed(1)}%`
      : "";
  push(vocab.split_label, "", "", "", "", "", pct);
  push("");
  push(
    vocab.disbursement_label,
    "",
    "",
    "",
    "",
    "",
    dollars(disb.advisor_disbursement_cents),
  );

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `disbursement-${disb.id.slice(0, 8)}-${today.replace(/\//g, "-")}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function StatusPill({ status }: { status: string }) {
  const palettes: Record<string, { bg: string; text: string }> = {
    pending: { bg: "var(--bg-1)", text: "var(--dark-gray)" },
    calculated: { bg: "var(--warning-bg)", text: "var(--navy)" },
    approved: { bg: "var(--blue-tint)", text: "var(--navy)" },
    paid: { bg: "var(--success-bg)", text: "var(--navy)" },
    cancelled: { bg: "var(--danger-bg)", text: "var(--navy)" },
    adjusted: { bg: "var(--blue-tint)", text: "var(--navy)" },
  };
  const p = palettes[status] ?? { bg: "var(--mid-gray)", text: "var(--dark-gray)" };
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        background: p.bg,
        color: p.text,
      }}
    >
      {status}
    </span>
  );
}

const th: React.CSSProperties = { padding: "10px 12px", fontWeight: 700 };
const td: React.CSSProperties = { padding: "10px 12px", fontSize: 13 };
const selectStyle: React.CSSProperties = {
  padding: "6px 12px",
  fontSize: 13,
  border: "1px solid var(--mid-gray)",
  borderRadius: 6,
};

function sourceLinkButtonLabel(kind: DisbursementSourceLinkRec["kind"]): string {
  switch (kind) {
    case "processing":
      return "Back to Processing record";
    case "opportunity":
      return "Back to Opportunity";
    case "household":
      return "Back to household";
  }
}

function pageBtn(variant: "primary" | "ghost"): React.CSSProperties {
  if (variant === "primary") {
    return {
      padding: "8px 16px",
      fontSize: 12,
      fontWeight: 700,
      borderRadius: 6,
      background: "var(--navy-surface)",
      color: "white",
      border: "none",
      cursor: "pointer",
    };
  }
  return {
    padding: "8px 16px",
    fontSize: 12,
    borderRadius: 6,
    background: "var(--bg-0)",
    color: "var(--blue-dark)",
    border: "1px solid var(--mid-gray)",
    cursor: "pointer",
  };
}
